import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import {
  InventoryOverview,
  InventoryOverviewParams,
  ProductInventoryDetails,
  InventoryAdjustment,
  BulkAdjustmentRequest,
  BulkAdjustmentResponse,
  InventoryReportParams,
  InventoryReportsResponse,
  InventoryAlertsResponse,
} from 'types/inventory';

export const initialState: State = {};

const slice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'inventoryApi',
  baseQuery,
  tagTypes: [
    'Inventory',
    'InventoryOverview',
    'ProductInventory',
    'InventoryAlerts',
    'InventoryReports',
  ],
  endpoints: builder => ({
    // Get Inventory Overview
    getInventoryOverview: builder.query<
      InventoryOverview,
      InventoryOverviewParams
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.supplierId)
          searchParams.append('supplierId', params.supplierId.toString());
        if (params.categoryId)
          searchParams.append('categoryId', params.categoryId.toString());
        if (params.lowStockThreshold)
          searchParams.append(
            'lowStockThreshold',
            params.lowStockThreshold.toString(),
          );

        const queryString = searchParams.toString();
        return {
          url: `${endpoints.INVENTORY_OVERVIEW}${queryString ? `?${queryString}` : ''
            }`,
          method: 'GET',
        };
      },
      providesTags: ['InventoryOverview'],
      transformResponse: (response: any) => {
        // Extract data from API wrapper
        return response?.data || response;
      },
    }),

    // Get Product Inventory Details
    getProductInventoryDetails: builder.query<ProductInventoryDetails, number>({
      query: productId => ({
        url: `${endpoints.INVENTORY_PRODUCT_DETAILS}/${productId}`,
        method: 'GET',
      }),
      providesTags: (result, error, productId) => [
        { type: 'ProductInventory', id: productId },
      ],
      transformResponse: (response: any) => {
        // Extract data from API wrapper
        return response?.data || response;
      },
    }),

    // Adjust Product Inventory
    adjustProductInventory: builder.mutation<
      { product: any; adjustment: InventoryAdjustment },
      { productId: number; adjustment: InventoryAdjustment }
    >({
      query: ({ productId, adjustment }) => ({
        url: `${endpoints.INVENTORY_ADJUST}/${productId}/adjust`,
        method: 'POST',
        body: adjustment,
      }),
      invalidatesTags: (result, error, { productId }) => [
        'InventoryOverview',
        { type: 'ProductInventory', id: productId },
        'InventoryAlerts',
      ],
    }),

    // Bulk Inventory Adjustment
    bulkAdjustInventory: builder.mutation<
      BulkAdjustmentResponse,
      BulkAdjustmentRequest
    >({
      query: data => ({
        url: endpoints.INVENTORY_BULK_ADJUST,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['InventoryOverview', 'InventoryAlerts'],
    }),

    // Get Inventory Reports
    getInventoryReports: builder.query<
      InventoryReportsResponse,
      InventoryReportParams
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.reportType)
          searchParams.append('reportType', params.reportType);
        if (params.supplierId)
          searchParams.append('supplierId', params.supplierId.toString());
        if (params.categoryId)
          searchParams.append('categoryId', params.categoryId.toString());
        if (params.startDate)
          searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        if (params.actionType)
          searchParams.append('actionType', params.actionType);

        const queryString = searchParams.toString();
        return {
          url: `${endpoints.INVENTORY_REPORTS}${queryString ? `?${queryString}` : ''
            }`,
          method: 'GET',
        };
      },
      providesTags: ['InventoryReports'],
      transformResponse: (response: any) => {
        // Extract data from API wrapper
        return response?.data || response;
      },
    }),

    // Get Inventory Alerts
    getInventoryAlerts: builder.query<
      InventoryAlertsResponse,
      { threshold?: number }
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.threshold)
          searchParams.append('threshold', params.threshold.toString());

        const queryString = searchParams.toString();
        return {
          url: `${endpoints.INVENTORY_ALERTS}${queryString ? `?${queryString}` : ''
            }`,
          method: 'GET',
        };
      },
      providesTags: ['InventoryAlerts'],
      transformResponse: (response: any) => {
        // Extract data from API wrapper
        return response?.data || response;
      },
    }),
  }),
});

export const { actions: InventoryActions } = slice;

export const useInventorySlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: InventoryActions,
    useGetInventoryOverviewQuery: api.useGetInventoryOverviewQuery,
    useGetProductInventoryDetailsQuery: api.useGetProductInventoryDetailsQuery,
    useAdjustProductInventoryMutation: api.useAdjustProductInventoryMutation,
    useBulkAdjustInventoryMutation: api.useBulkAdjustInventoryMutation,
    useGetInventoryReportsQuery: api.useGetInventoryReportsQuery,
    useGetInventoryAlertsQuery: api.useGetInventoryAlertsQuery,
  };
};

// Export the API for direct use (for components that don't use the slice)
export const {
  useGetInventoryOverviewQuery: useGetInventoryOverviewQueryDirect,
  useGetProductInventoryDetailsQuery: useGetProductInventoryDetailsQueryDirect,
  useAdjustProductInventoryMutation: useAdjustProductInventoryMutationDirect,
  useBulkAdjustInventoryMutation: useBulkAdjustInventoryMutationDirect,
  useGetInventoryReportsQuery: useGetInventoryReportsQueryDirect,
  useGetInventoryAlertsQuery: useGetInventoryAlertsQueryDirect,
} = api;
